import React, { useState } from 'react';
import LaunchMonitorImportReview from './LaunchMonitorImportReview';

interface LaunchMonitorFieldsProps {
  clubType: 'driver' | 'iron' | 'wedge';
  formData: any;
  updateNestedField: (section: any, updates: any) => void;
  getPriorityClasses: (path: string, isEmpty: boolean) => string;
}

const SECTION_FOR_CLUB: Record<string, string> = {
  driver: 'launch_monitor_driver',
  iron: 'launch_monitor_iron',
  wedge: 'launch_monitor_wedge',
};

const LaunchMonitorFields: React.FC<LaunchMonitorFieldsProps> = ({
  clubType,
  formData,
  updateNestedField,
  getPriorityClasses
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const handleImportConfirm = (fields: Record<string, number | undefined>) => {
    const section = SECTION_FOR_CLUB[clubType];
    // Filter out undefined values before updating
    const updates: Record<string, number> = {};
    for (const [key, val] of Object.entries(fields)) {
      if (val !== undefined && !isNaN(val)) updates[key] = val;
    }
    updateNestedField(section, updates);
    setShowImport(false);
  };

  const importButton = (
    <div className="flex justify-end mb-3">
      <button
        type="button"
        onClick={() => setShowImport(true)}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-green-ink bg-green/10 border border-green-ink rounded-lg hover:bg-green/15 transition-colors"
      >
        <span>📥</span>
        Import from Launch Monitor
      </button>
    </div>
  );

  if (clubType === 'driver') {
    const lmData = formData.launch_monitor_driver || {};

    return (
      <>
        {showImport && (
          <LaunchMonitorImportReview
            clubType="driver"
            onConfirm={handleImportConfirm}
            onClose={() => setShowImport(false)}
          />
        )}
      <div className="space-y-6">
        {importButton}
        {/* Core Metrics */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">📊 Driver - Core Launch Monitor Metrics</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Launch Angle + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Launch Angle (avg, degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="8"
                max="18"
                step="0.1"
                value={lmData.launch_angle_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  launch_angle_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.launch_angle_driver', !lmData.launch_angle_driver)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Average vertical launch angle</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Launch Angle Std Dev
              </label>
              <input
                type="number"
                min="0.5"
                max="5"
                step="0.1"
                value={lmData.launch_angle_std_dev_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  launch_angle_std_dev_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;2°)</p>
            </div>

            {/* Spin Rate + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Rate (avg, rpm) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1500"
                max="4000"
                value={lmData.spin_rate_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  spin_rate_driver: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.spin_rate_driver', !lmData.spin_rate_driver)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Optimal: 2000-2800 rpm</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Rate Std Dev
              </label>
              <input
                type="number"
                min="100"
                max="800"
                value={lmData.spin_rate_std_dev_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  spin_rate_std_dev_driver: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;300 rpm)</p>
            </div>

            {/* Ball Speed + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Ball Speed (avg, mph) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="120"
                max="185"
                step="0.1"
                value={lmData.ball_speed_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  ball_speed_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.ball_speed_driver', !lmData.ball_speed_driver)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Every 1 mph ≈ 2 yards</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Ball Speed Std Dev
              </label>
              <input
                type="number"
                min="1"
                max="8"
                step="0.1"
                value={lmData.ball_speed_std_dev_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  ball_speed_std_dev_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;3 mph)</p>
            </div>

            {/* Carry Distance + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Carry Distance (avg, yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="180"
                max="320"
                value={lmData.driver_carry_distance || lmData.carry_distance_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  driver_carry_distance: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.driver_carry_distance', !lmData.driver_carry_distance)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Air distance (most important)</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Carry Std Dev
              </label>
              <input
                type="number"
                min="3"
                max="25"
                step="0.1"
                value={lmData.carry_std_dev_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  carry_std_dev_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;10 yds)</p>
            </div>

            {/* Face-to-Path */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Face-to-Path (degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-10"
                max="10"
                step="0.1"
                value={lmData.face_to_path_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  face_to_path_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.face_to_path_driver', !lmData.face_to_path_driver)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Positive = open face (slice spin)</p>
            </div>

            {/* Spin Axis - CORRECTED TO DEGREES */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Axis (degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-20"
                max="20"
                step="0.1"
                value={lmData.spin_axis_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  spin_axis_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.spin_axis_driver', !lmData.spin_axis_driver)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Tilt angle: + = right, - = left (NOT rpm!)</p>
            </div>

            {/* Side Deviation */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Side Deviation (yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-50"
                max="50"
                step="0.1"
                value={lmData.side_deviation_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  side_deviation_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.side_deviation_driver', !lmData.side_deviation_driver)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Avg left/right miss (+ = right)</p>
            </div>

            {/* Dispersion Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Dispersion Std Dev (yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="5"
                max="40"
                step="0.1"
                value={lmData.dispersion_std_dev_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  dispersion_std_dev_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.dispersion_std_dev_driver', !lmData.dispersion_std_dev_driver)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Shot scatter (lower = tighter)</p>
            </div>

            {/* Smash Factor */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Smash Factor <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1.40"
                max="1.52"
                step="0.01"
                value={lmData.smash_factor_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  smash_factor_driver: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_driver.smash_factor_driver', !lmData.smash_factor_driver)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 1.48-1.50 = excellent</p>
            </div>

            {/* Impact Location Pattern */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Impact Location Pattern
              </label>
              <select
                value={lmData.impact_location_pattern_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  impact_location_pattern_driver: e.target.value || undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select pattern...</option>
                <option value="Center">Center</option>
                <option value="Heel">Heel</option>
                <option value="Toe">Toe</option>
                <option value="High">High</option>
                <option value="Low">Low</option>
                <option value="Heel-High">Heel-High</option>
                <option value="Heel-Low">Heel-Low</option>
                <option value="Toe-High">Toe-High</option>
                <option value="Toe-Low">Toe-Low</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">💡 Where you typically contact</p>
            </div>

            {/* Shot Count */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Shot Count
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={lmData.shot_count_driver || ''}
                onChange={(e) => updateNestedField('launch_monitor_driver', {
                  shot_count_driver: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 10-20 shots recommended</p>
            </div>
          </div>
        </div>

        {/* Advanced Metrics - Collapsible */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 font-medium text-gray-900 mb-3 hover:text-green-ink"
          >
            <span>{showAdvanced ? '▼' : '▶'}</span>
            Advanced Metrics (Optional)
          </button>

          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Smash Factor
                </label>
                <input
                  type="number"
                  min="1.30"
                  max="1.55"
                  step="0.01"
                  value={lmData.smash_factor_driver || ''}
                  onChange={(e) => updateNestedField('launch_monitor_driver', {
                    smash_factor_driver: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 1.48+ is excellent</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Side Deviation (yards)
                </label>
                <input
                  type="number"
                  min="-50"
                  max="50"
                  step="0.1"
                  value={lmData.side_deviation_driver || ''}
                  onChange={(e) => updateNestedField('launch_monitor_driver', {
                    side_deviation_driver: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Launch Direction (degrees)
                </label>
                <input
                  type="number"
                  min="-20"
                  max="20"
                  step="0.1"
                  value={lmData.launch_direction_driver || ''}
                  onChange={(e) => updateNestedField('launch_monitor_driver', {
                    launch_direction_driver: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Carry Std Dev (yards)
                </label>
                <input
                  type="number"
                  min="5"
                  max="30"
                  step="0.1"
                  value={lmData.carry_std_dev_driver || ''}
                  onChange={(e) => updateNestedField('launch_monitor_driver', {
                    carry_std_dev_driver: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Avg Smash Factor
                </label>
                <input
                  type="number"
                  min="1.30"
                  max="1.55"
                  step="0.01"
                  value={lmData.avg_smash_factor_driver || ''}
                  onChange={(e) => updateNestedField('launch_monitor_driver', {
                    avg_smash_factor_driver: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Average across all shots</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Impact Location Pattern
                </label>
                <select
                  value={lmData.impact_location_pattern_driver || ''}
                  onChange={(e) => updateNestedField('launch_monitor_driver', {
                    impact_location_pattern_driver: e.target.value || undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                >
                  <option value="">Select pattern...</option>
                  <option value="center">Center</option>
                  <option value="heel">Heel</option>
                  <option value="toe">Toe</option>
                  <option value="high">High</option>
                  <option value="low">Low</option>
                  <option value="heel-high">Heel-High</option>
                  <option value="heel-low">Heel-Low</option>
                  <option value="toe-high">Toe-High</option>
                  <option value="toe-low">Toe-Low</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">💡 Where you typically contact the face</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Peak Height (yards)
                </label>
                <input
                  type="number"
                  min="20"
                  max="60"
                  step="0.1"
                  value={lmData.peak_height_driver || ''}
                  onChange={(e) => updateNestedField('launch_monitor_driver', {
                    peak_height_driver: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Maximum ball height</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Descent Angle (degrees)
                </label>
                <input
                  type="number"
                  min="30"
                  max="50"
                  step="0.1"
                  value={lmData.descent_angle_driver || ''}
                  onChange={(e) => updateNestedField('launch_monitor_driver', {
                    descent_angle_driver: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Steeper = more stopping power</p>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    );
  }

  if (clubType === 'iron') {
    const lmData = formData.launch_monitor_iron || {};

    return (
      <>
        {showImport && (
          <LaunchMonitorImportReview
            clubType="iron"
            onConfirm={handleImportConfirm}
            onClose={() => setShowImport(false)}
          />
        )}
      <div className="space-y-6">
        {importButton}
        {/* Core Metrics */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">📊 7-Iron - Core Launch Monitor Metrics</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Launch Angle + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Launch Angle (avg, degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="12"
                max="22"
                step="0.1"
                value={lmData.launch_angle_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  launch_angle_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.launch_angle_7i', !lmData.launch_angle_7i)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Optimal: 15-18° for 7-iron</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Launch Angle Std Dev
              </label>
              <input
                type="number"
                min="0.5"
                max="5"
                step="0.1"
                value={lmData.launch_angle_std_dev_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  launch_angle_std_dev_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;2°)</p>
            </div>

            {/* Spin Rate + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Rate (avg, rpm) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="4000"
                max="8500"
                value={lmData.spin_rate_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  spin_rate_7i: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.spin_rate_7i', !lmData.spin_rate_7i)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Optimal: 6000-7000 rpm</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Rate Std Dev
              </label>
              <input
                type="number"
                min="100"
                max="1000"
                value={lmData.spin_rate_std_dev_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  spin_rate_std_dev_7i: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;400 rpm)</p>
            </div>

            {/* Ball Speed + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Ball Speed (avg, mph) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="85"
                max="135"
                step="0.1"
                value={lmData.ball_speed_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  ball_speed_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.ball_speed_7i', !lmData.ball_speed_7i)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Tour avg: ~110 mph</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Ball Speed Std Dev
              </label>
              <input
                type="number"
                min="1"
                max="8"
                step="0.1"
                value={lmData.ball_speed_std_dev_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  ball_speed_std_dev_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;3 mph)</p>
            </div>

            {/* Carry Distance + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Carry Distance (avg, yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="100"
                max="200"
                value={lmData.iron_carry_distance || lmData.carry_distance_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  iron_carry_distance: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.iron_carry_distance', !lmData.iron_carry_distance)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Air distance (most important)</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Carry Std Dev
              </label>
              <input
                type="number"
                min="3"
                max="25"
                step="0.1"
                value={lmData.carry_std_dev_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  carry_std_dev_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;8 yds)</p>
            </div>

            {/* Face-to-Path */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Face-to-Path (degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-10"
                max="10"
                step="0.1"
                value={lmData.face_to_path_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  face_to_path_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.face_to_path_7i', !lmData.face_to_path_7i)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Positive = open face (slice spin)</p>
            </div>

            {/* Spin Axis - CORRECTED TO DEGREES */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Axis (degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-20"
                max="20"
                step="0.1"
                value={lmData.spin_axis_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  spin_axis_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.spin_axis_7i', !lmData.spin_axis_7i)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Tilt angle: + = right, - = left (NOT rpm!)</p>
            </div>

            {/* Side Deviation */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Side Deviation (yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-40"
                max="40"
                step="0.1"
                value={lmData.side_deviation_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  side_deviation_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.side_deviation_7i', !lmData.side_deviation_7i)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Avg left/right miss (+ = right)</p>
            </div>

            {/* Dispersion Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Dispersion Std Dev (yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="5"
                max="30"
                step="0.1"
                value={lmData.dispersion_std_dev_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  dispersion_std_dev_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.dispersion_std_dev_7i', !lmData.dispersion_std_dev_7i)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Shot scatter (lower = tighter)</p>
            </div>

            {/* Smash Factor */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Smash Factor <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1.20"
                max="1.45"
                step="0.01"
                value={lmData.smash_factor_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  smash_factor_7i: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_iron.smash_factor_7i', !lmData.smash_factor_7i)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 1.38+ is excellent</p>
            </div>

            {/* Impact Location Pattern */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Impact Location Pattern
              </label>
              <select
                value={lmData.impact_location_pattern_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  impact_location_pattern_7i: e.target.value || undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select pattern...</option>
                <option value="Center">Center</option>
                <option value="Heel">Heel</option>
                <option value="Toe">Toe</option>
                <option value="High">High</option>
                <option value="Low">Low</option>
                <option value="Heel-High">Heel-High</option>
                <option value="Heel-Low">Heel-Low</option>
                <option value="Toe-High">Toe-High</option>
                <option value="Toe-Low">Toe-Low</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">💡 Where you typically contact</p>
            </div>

            {/* Shot Count */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Shot Count
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={lmData.shot_count_7i || ''}
                onChange={(e) => updateNestedField('launch_monitor_iron', {
                  shot_count_7i: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 10-20 shots recommended</p>
            </div>
          </div>
        </div>

        {/* Advanced Metrics - Collapsible */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 font-medium text-gray-900 mb-3 hover:text-green-ink"
          >
            <span>{showAdvanced ? '▼' : '▶'}</span>
            Advanced Metrics (Optional)
          </button>

          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Smash Factor
                </label>
                <input
                  type="number"
                  min="1.20"
                  max="1.45"
                  step="0.01"
                  value={lmData.smash_factor_7i || ''}
                  onChange={(e) => updateNestedField('launch_monitor_iron', {
                    smash_factor_7i: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 1.38+ is excellent</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Side Deviation (yards)
                </label>
                <input
                  type="number"
                  min="-40"
                  max="40"
                  step="0.1"
                  value={lmData.side_deviation_7i || ''}
                  onChange={(e) => updateNestedField('launch_monitor_iron', {
                    side_deviation_7i: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Launch Direction (degrees)
                </label>
                <input
                  type="number"
                  min="-20"
                  max="20"
                  step="0.1"
                  value={lmData.launch_direction_7i || ''}
                  onChange={(e) => updateNestedField('launch_monitor_iron', {
                    launch_direction_7i: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Carry Std Dev (yards)
                </label>
                <input
                  type="number"
                  min="5"
                  max="25"
                  step="0.1"
                  value={lmData.carry_std_dev_7i || ''}
                  onChange={(e) => updateNestedField('launch_monitor_iron', {
                    carry_std_dev_7i: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Avg Smash Factor
                </label>
                <input
                  type="number"
                  min="1.20"
                  max="1.45"
                  step="0.01"
                  value={lmData.avg_smash_factor_7i || ''}
                  onChange={(e) => updateNestedField('launch_monitor_iron', {
                    avg_smash_factor_7i: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Average across all shots</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Impact Location Pattern
                </label>
                <select
                  value={lmData.impact_location_pattern_7i || ''}
                  onChange={(e) => updateNestedField('launch_monitor_iron', {
                    impact_location_pattern_7i: e.target.value || undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                >
                  <option value="">Select pattern...</option>
                  <option value="center">Center</option>
                  <option value="heel">Heel</option>
                  <option value="toe">Toe</option>
                  <option value="high">High</option>
                  <option value="low">Low</option>
                  <option value="heel-high">Heel-High</option>
                  <option value="heel-low">Heel-Low</option>
                  <option value="toe-high">Toe-High</option>
                  <option value="toe-low">Toe-Low</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">💡 Where you typically contact the face</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Peak Height (yards)
                </label>
                <input
                  type="number"
                  min="20"
                  max="50"
                  step="0.1"
                  value={lmData.peak_height_7i || ''}
                  onChange={(e) => updateNestedField('launch_monitor_iron', {
                    peak_height_7i: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Maximum ball height</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Descent Angle (degrees)
                </label>
                <input
                  type="number"
                  min="40"
                  max="55"
                  step="0.1"
                  value={lmData.descent_angle_7i || ''}
                  onChange={(e) => updateNestedField('launch_monitor_iron', {
                    descent_angle_7i: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Steeper = more stopping power</p>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    );
  }

  if (clubType === 'wedge') {
    const lmData = formData.launch_monitor_wedge || {};

    return (
      <>
        {showImport && (
          <LaunchMonitorImportReview
            clubType="wedge"
            onConfirm={handleImportConfirm}
            onClose={() => setShowImport(false)}
          />
        )}
      <div className="space-y-6">
        {importButton}
        {/* Core Metrics */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">📊 Wedge - Core Launch Monitor Metrics</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Launch Angle + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Launch Angle (avg, degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="20"
                max="35"
                step="0.1"
                value={lmData.launch_angle_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  launch_angle_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.launch_angle_wedge', !lmData.launch_angle_wedge)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Optimal: 24-28° for wedge</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Launch Angle Std Dev
              </label>
              <input
                type="number"
                min="0.5"
                max="5"
                step="0.1"
                value={lmData.launch_angle_std_dev_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  launch_angle_std_dev_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;2°)</p>
            </div>

            {/* Spin Rate + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Rate (avg, rpm) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="6000"
                max="12000"
                value={lmData.spin_rate_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  spin_rate_wedge: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.spin_rate_wedge', !lmData.spin_rate_wedge)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Optimal: 8000-10000 rpm</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Rate Std Dev
              </label>
              <input
                type="number"
                min="200"
                max="1500"
                value={lmData.spin_rate_std_dev_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  spin_rate_std_dev_wedge: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;600 rpm)</p>
            </div>

            {/* Ball Speed + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Ball Speed (avg, mph) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="60"
                max="110"
                step="0.1"
                value={lmData.ball_speed_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  ball_speed_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.ball_speed_wedge', !lmData.ball_speed_wedge)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Full swing: 70-95 mph</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Ball Speed Std Dev
              </label>
              <input
                type="number"
                min="1"
                max="8"
                step="0.1"
                value={lmData.ball_speed_std_dev_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  ball_speed_std_dev_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;3 mph)</p>
            </div>

            {/* Carry Distance + Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Carry Distance (avg, yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="50"
                max="140"
                value={lmData.wedge_carry_distance || lmData.carry_distance_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  wedge_carry_distance: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.wedge_carry_distance', !lmData.wedge_carry_distance)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Air distance (most important)</p>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Carry Std Dev
              </label>
              <input
                type="number"
                min="2"
                max="20"
                step="0.1"
                value={lmData.carry_std_dev_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  carry_std_dev_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Consistency (target: &lt;6 yds)</p>
            </div>

            {/* Face-to-Path */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Face-to-Path (degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-10"
                max="10"
                step="0.1"
                value={lmData.face_to_path_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  face_to_path_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.face_to_path_wedge', !lmData.face_to_path_wedge)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Positive = open face (slice spin)</p>
            </div>

            {/* Spin Axis - CORRECTED TO DEGREES */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Spin Axis (degrees) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-20"
                max="20"
                step="0.1"
                value={lmData.spin_axis_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  spin_axis_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.spin_axis_wedge', !lmData.spin_axis_wedge)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Tilt angle: + = right, - = left (NOT rpm!)</p>
            </div>

            {/* Side Deviation */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Side Deviation (yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="-30"
                max="30"
                step="0.1"
                value={lmData.side_deviation_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  side_deviation_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.side_deviation_wedge', !lmData.side_deviation_wedge)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Avg left/right miss (+ = right)</p>
            </div>

            {/* Dispersion Std Dev */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Dispersion Std Dev (yards) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="3"
                max="25"
                step="0.1"
                value={lmData.dispersion_std_dev_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  dispersion_std_dev_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.dispersion_std_dev_wedge', !lmData.dispersion_std_dev_wedge)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Shot scatter (lower = tighter)</p>
            </div>

            {/* Smash Factor */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Smash Factor <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1.00"
                max="1.30"
                step="0.01"
                value={lmData.smash_factor_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  smash_factor_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('launch_monitor_wedge.smash_factor_wedge', !lmData.smash_factor_wedge)}
              />
              <p className="text-xs text-gray-500 mt-1">💡 1.20+ is excellent</p>
            </div>

            {/* Impact Location Pattern */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Impact Location Pattern
              </label>
              <select
                value={lmData.impact_location_pattern_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  impact_location_pattern_wedge: e.target.value || undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select pattern...</option>
                <option value="Center">Center</option>
                <option value="Heel">Heel</option>
                <option value="Toe">Toe</option>
                <option value="High">High</option>
                <option value="Low">Low</option>
                <option value="Heel-High">Heel-High</option>
                <option value="Heel-Low">Heel-Low</option>
                <option value="Toe-High">Toe-High</option>
                <option value="Toe-Low">Toe-Low</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">💡 Where you typically contact</p>
            </div>

            {/* Shot Count */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Shot Count
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={lmData.shot_count_wedge || ''}
                onChange={(e) => updateNestedField('launch_monitor_wedge', {
                  shot_count_wedge: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
              />
              <p className="text-xs text-gray-500 mt-1">💡 10-20 shots recommended</p>
            </div>
          </div>
        </div>

        {/* Advanced Metrics - Collapsible */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 font-medium text-gray-900 mb-3 hover:text-green-ink"
          >
            <span>{showAdvanced ? '▼' : '▶'}</span>
            Advanced Metrics (Optional)
          </button>

          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Smash Factor
                </label>
                <input
                  type="number"
                  min="1.00"
                  max="1.30"
                  step="0.01"
                  value={lmData.smash_factor_wedge || ''}
                  onChange={(e) => updateNestedField('launch_monitor_wedge', {
                    smash_factor_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 1.20+ is excellent</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Side Deviation (yards)
                </label>
                <input
                  type="number"
                  min="-30"
                  max="30"
                  step="0.1"
                  value={lmData.side_deviation_wedge || ''}
                  onChange={(e) => updateNestedField('launch_monitor_wedge', {
                    side_deviation_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Launch Direction (degrees)
                </label>
                <input
                  type="number"
                  min="-20"
                  max="20"
                  step="0.1"
                  value={lmData.launch_direction_wedge || ''}
                  onChange={(e) => updateNestedField('launch_monitor_wedge', {
                    launch_direction_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Carry Std Dev (yards)
                </label>
                <input
                  type="number"
                  min="5"
                  max="20"
                  step="0.1"
                  value={lmData.carry_std_dev_wedge || ''}
                  onChange={(e) => updateNestedField('launch_monitor_wedge', {
                    carry_std_dev_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Avg Smash Factor
                </label>
                <input
                  type="number"
                  min="1.00"
                  max="1.30"
                  step="0.01"
                  value={lmData.avg_smash_factor_wedge || ''}
                  onChange={(e) => updateNestedField('launch_monitor_wedge', {
                    avg_smash_factor_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Average across all shots</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Impact Location Pattern
                </label>
                <select
                  value={lmData.impact_location_pattern_wedge || ''}
                  onChange={(e) => updateNestedField('launch_monitor_wedge', {
                    impact_location_pattern_wedge: e.target.value || undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                >
                  <option value="">Select pattern...</option>
                  <option value="center">Center</option>
                  <option value="heel">Heel</option>
                  <option value="toe">Toe</option>
                  <option value="high">High</option>
                  <option value="low">Low</option>
                  <option value="heel-high">Heel-High</option>
                  <option value="heel-low">Heel-Low</option>
                  <option value="toe-high">Toe-High</option>
                  <option value="toe-low">Toe-Low</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">💡 Where you typically contact the face</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Peak Height (yards)
                </label>
                <input
                  type="number"
                  min="15"
                  max="40"
                  step="0.1"
                  value={lmData.peak_height_wedge || ''}
                  onChange={(e) => updateNestedField('launch_monitor_wedge', {
                    peak_height_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Maximum ball height</p>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Descent Angle (degrees)
                </label>
                <input
                  type="number"
                  min="45"
                  max="60"
                  step="0.1"
                  value={lmData.descent_angle_wedge || ''}
                  onChange={(e) => updateNestedField('launch_monitor_wedge', {
                    descent_angle_wedge: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Steeper = more stopping power</p>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    );
  }

  return null;
};

export default LaunchMonitorFields;
